/**
 * React Query hooks for the Alerts workspace.
 *
 * Two independent namespaces (`alerts-queue` / `alerts-history`) so neither can
 * disturb the other's cache. Both keep the previous page visible while the next
 * one loads — an operator watching the queue must never have the table replaced
 * by a spinner on a background refresh.
 *
 * Stale-response protection comes from React Query itself: the key encodes the
 * full request identity (scope, filters, cursor/offset), so a slow response for
 * a scope the user has already left resolves into ITS OWN cache entry and can
 * never overwrite the current one. `signal` is forwarded so those in-flight
 * requests are also aborted.
 */
import { useEffect, useRef } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAlertListQueryKey } from "@/alerts/api/queries/machine-alerts.queries";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import {
  getAlertHistory,
  getAlertQueue,
  buildAlertHistoryParams,
  buildAlertQueueParams,
} from "@/alerts/api/requests/alerts-workspace.requests.ts";
import {
  getAlertActivities,
  getAlertWorkspaceDetail,
  postAlertAction,
} from "@/alerts/api/requests/alert-workflow.requests.ts";
import { AlertApiError, alertWorkflowFailure } from "@/alerts/api/envelope.ts";
import { useAlertLiveStore } from "@/alerts/stores/use-alert-live-store.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { AlertStatus } from "@/alerts/types/machine-alert.ts";
import type {
  AlertActionRequest,
  AlertActionResult,
  AlertActivity,
  AlertWorkspaceDetail,
  AlertHistoryFilterState,
  AlertHistoryPage,
  AlertQueueFilterState,
  AlertQueuePage,
} from "@/alerts/types/alerts-workspace.ts";

/** The queue is live work — re-read it on this cadence. */
export const QUEUE_POLL_MS = 20_000;

/**
 * Queue read. Enabled only with a location: the contract requires one, and
 * asking for "all Pending everywhere" is not a supported scope.
 */
export const useAlertQueue = (
  state: AlertQueueFilterState | null,
  /**
   * Poll cadence. The workspace table is the operator's live work list and keeps
   * the default; the header bell is a badge and passes something slower, because
   * a queue page is a large response and the bell reads two numbers off it.
   */
  pollMs: number = QUEUE_POLL_MS,
) => {
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);

  const query = useQuery<AlertQueuePage>({
    // The built params ARE the request identity — deriving the key from them
    // means a filter that does not reach the wire cannot split the cache either.
    queryKey: [
      QUERY_KEYS.ALERTS_QUEUE,
      organizationId,
      state ? buildAlertQueueParams(state) : null,
    ],
    queryFn: ({ signal }) => getAlertQueue(state as AlertQueueFilterState, signal),
    enabled: state != null,
    refetchInterval: pollMs,
    // Background refresh keeps the last good page on screen.
    placeholderData: keepPreviousData,
    // Auth failures are handled by the global interceptor; retrying them here
    // just multiplies the noise.
    retry: (count, error) =>
      count < 2 && !(error as { status?: number })?.status?.toString().startsWith("4"),
  });

  // --- Live channel bridge --------------------------------------------------
  // A Firestore hint carries alert IDs only — never a row — so it cannot be
  // merged into the queue. It is a "something changed, re-read now" signal, and
  // re-reading THIS query is what turns a 20s poll into an immediate arrival.
  // The poll stays as the fallback the channel degrades to.
  //
  // Queue only: the contract is explicit that live reconciliation invalidates
  // the Queue and NOT History — a bounded historical search does not change
  // because a new alert was raised.
  //
  // Hints are diffed against the PREVIOUS id set rather than acted on wholesale:
  // the store's map is TTL-pruned and reset on scope changes, and neither must
  // look like a fresh hint and trigger a refetch loop. The first pass only
  // seeds, since anything recorded before mount is already in the initial read.
  const recentHints = useAlertLiveStore((s) => s.recentHints);
  const handledHintIdsRef = useRef<Set<string> | null>(null);
  const { refetch } = query;
  const enabled = state != null;
  useEffect(() => {
    const ids = Object.keys(recentHints);
    const handled = handledHintIdsRef.current;
    handledHintIdsRef.current = new Set(ids);
    if (handled == null || !enabled) return;
    if (!ids.some((id) => !handled.has(id))) return;
    void refetch();
  }, [recentHints, enabled, refetch]);

  return query;
};

/**
 * How far back the arrival probe looks, and how coarsely its clock is rounded.
 *
 * The probe exists because the Queue cannot answer "what just arrived". It is
 * ranked oldest-first with `limit` capped at 200 and rejects an `ordering`
 * parameter, so on a location with a 469-deep backlog the newest row on page one
 * is a week old — a brand-new alert is simply not on any page the bell holds.
 * History is the only endpoint that can sort newest-first, so a narrow rolling
 * read of it is the arrival detector. It feeds the ALARM, not the workload
 * figures: `count`, the badge total and the bulk of the bell list still come
 * from the Queue.
 *
 * The window is quantized because `start_time`/`end_time` are part of the query
 * key. Computing them from a raw `Date.now()` would mint a new key on every
 * render and spin the endpoint — a mistake already made once on this branch.
 */
export const PROBE_WINDOW_MS = 30 * 60_000;
export const PROBE_QUANTUM_MS = 60_000;

/**
 * Rolling "what arrived recently?" read, independent of the global date picker.
 *
 * Deliberately narrow: one location, Pending only, newest first, one small page.
 * It is not a history search and must never be widened into one — everything the
 * operator browses still goes through {@link useAlertHistory}.
 */
export const useRecentAlertProbe = (locationId: number | null, nowBucket: number) => {
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);

  const state: AlertHistoryFilterState | null =
    locationId == null
      ? null
      : {
          scope: { location_id: locationId },
          start_time: new Date(nowBucket - PROBE_WINDOW_MS),
          // A quantum past the bucket, so an alert stamped a few seconds ahead of
          // our clock is not invisible until the window rolls.
          end_time: new Date(nowBucket + PROBE_QUANTUM_MS),
          status_in: [AlertStatus.Pending],
          ordering: "-timestamp",
          limit: 50,
          offset: 0,
        };

  return useQuery<AlertHistoryPage>({
    queryKey: [
      QUERY_KEYS.ALERTS_HISTORY,
      "arrival-probe",
      organizationId,
      state ? buildAlertHistoryParams(state) : null,
    ],
    queryFn: ({ signal }) => getAlertHistory(state as AlertHistoryFilterState, signal),
    enabled: state != null,
    refetchInterval: QUEUE_POLL_MS,
    placeholderData: keepPreviousData,
    retry: (count, error) =>
      count < 2 && !(error as { status?: number })?.status?.toString().startsWith("4"),
  });
};

/** History read. Enabled only once both range boundaries exist. */
export const useAlertHistory = (state: AlertHistoryFilterState | null) => {
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);

  return useQuery<AlertHistoryPage>({
    queryKey: [
      QUERY_KEYS.ALERTS_HISTORY,
      organizationId,
      state ? buildAlertHistoryParams(state) : null,
    ],
    queryFn: ({ signal }) => getAlertHistory(state as AlertHistoryFilterState, signal),
    enabled: state != null,
    placeholderData: keepPreviousData,
    retry: (count, error) =>
      count < 2 && !(error as { status?: number })?.status?.toString().startsWith("4"),
  });
};

/* ------------------------------------------------------------------ *
 * Alert review + workflow actions (MIN-226)
 * ------------------------------------------------------------------ */

/**
 * Canonical detail. Deliberately its own cache entry, never derived from a list
 * row and never sharing one with the activities query — a failed background
 * refetch of one must not erase the other's last valid response.
 */
export const useAlertWorkspaceDetail = (alertId: number | null) =>
  useQuery<AlertWorkspaceDetail>({
    queryKey: [QUERY_KEYS.ALERT_DETAIL, alertId],
    queryFn: ({ signal }) => getAlertWorkspaceDetail(alertId as number, signal),
    enabled: alertId != null,
    // Keep the last good detail on screen through a refresh.
    placeholderData: keepPreviousData,
  });

/** The immutable workflow timeline, newest first. */
export const useAlertActivities = (alertId: number | null) =>
  useQuery<AlertActivity[]>({
    queryKey: [QUERY_KEYS.ALERT_ACTIVITIES, alertId],
    queryFn: ({ signal }) => getAlertActivities(alertId as number, signal),
    enabled: alertId != null,
    placeholderData: keepPreviousData,
  });

export interface AlertActionVariables {
  alertId: number;
  action: AlertActionRequest;
  /** From canonical detail — the version the operator was actually looking at. */
  workflowVersion: number | null;
  /**
   * Identifies the user's INTENT. The caller owns it so an exact retry of a
   * failed submission reuses the key (the server replays instead of applying
   * twice), while a fresh click mints a new one.
   */
  idempotencyKey: string;
}

/**
 * Perform a workflow action.
 *
 * Nothing here optimistically mutates alert state: REST is the canonical truth,
 * and inventing a status locally is how an operator ends up looking at a
 * decision the server refused. On success the detail, the timeline and the
 * active list are all re-read; queue membership is whatever the refreshed queue
 * says it is, so a row is never removed locally just because a POST returned
 * 200.
 *
 * On failure nothing is retried — every workflow failure needs the operator to
 * look again (see {@link alertWorkflowFailure}). The reconciling ones still
 * re-read canonical state so what they are looking at is current.
 */
export const useAlertAction = () => {
  const queryClient = useQueryClient();

  const reconcile = (alertId: number, { includeDetail = true } = {}) => {
    if (includeDetail) {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALERT_DETAIL, alertId] });
    }
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALERT_ACTIVITIES, alertId] });
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALERTS_QUEUE] });
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALERTS_HISTORY] });
    // The MAP reads alerts through its own list keys, not the workspace's queue
    // and history. Left out, a verdict given on the map left the map's own feed
    // showing the old status until something else happened to refetch it.
    void queryClient.invalidateQueries({ predicate: (query) => isAlertListQueryKey(query.queryKey) });
  };

  return useMutation<AlertActionResult, AlertApiError, AlertActionVariables>({
    mutationFn: ({ alertId, action, workflowVersion, idempotencyKey }) =>
      postAlertAction(alertId, action, { idempotencyKey, workflowVersion }),
    // A workflow write is a decision, not a fetch — retrying one is never safe.
    retry: false,
    onSuccess: (_result, { alertId }) => reconcile(alertId),
    onError: (error, { alertId }) => {
      const failure = alertWorkflowFailure(error);
      // These mean "your view of the alert is out of date" — pull the truth.
      // An idempotency clash changed nothing, so there is nothing to reconcile.
      if (
        failure === "alert_state_changed" ||
        failure === "workflow_version_required" ||
        failure === "invalid_transition"
      ) {
        reconcile(alertId);
      }
      // The alert is gone: its detail cache is meaningless, but the lists must
      // still refresh so the stale row disappears.
      if (failure === "alert_not_found") {
        queryClient.removeQueries({ queryKey: [QUERY_KEYS.ALERT_DETAIL, alertId] });
        reconcile(alertId, { includeDetail: false });
      }
    },
  });
};
